import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  RepeaterDetail,
  RepeaterListResponse,
  CreateRepeaterRequest,
  UpdateRepeaterRequest,
} from '../types/repeater';

export const repeaterKeys = {
  all: ['repeaters'] as const,
  list: () => [...repeaterKeys.all, 'list'] as const,
  detail: (id: string) => [...repeaterKeys.all, 'detail', id] as const,
};

export function useRepeaters() {
  return useQuery({
    queryKey: repeaterKeys.list(),
    queryFn: () => apiFetch<RepeaterListResponse>('/api/repeaters'),
    staleTime: 30_000,
  });
}

export function useRepeater(id: string | null) {
  return useQuery({
    queryKey: repeaterKeys.detail(id ?? ''),
    queryFn: () => apiFetch<RepeaterDetail>(`/api/repeaters/${id}`),
    enabled: !!id,
  });
}

export function useCreateRepeater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRepeaterRequest) =>
      apiFetch<RepeaterDetail>('/api/repeaters', {
        method: 'POST',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repeaterKeys.list() });
    },
  });
}

export function useUpdateRepeater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateRepeaterRequest & { id: string }) =>
      apiFetch<RepeaterDetail>(`/api/repeaters/${id}`, {
        method: 'PUT',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: repeaterKeys.list() });
      queryClient.invalidateQueries({ queryKey: repeaterKeys.detail(variables.id) });
    },
  });
}

export function useDeleteRepeater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/repeaters/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repeaterKeys.list() });
    },
  });
}

export function useActivateRepeater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/repeaters/${id}/activate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repeaterKeys.list() });
    },
  });
}

export function useDeactivateRepeater() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<void>('/api/repeaters/deactivate', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: repeaterKeys.list() });
    },
  });
}
